import { useState, useMemo, useCallback, useEffect } from 'react';

import { CheckCircle, ExternalLink, FileSpreadsheet, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { AuthMethodTabs } from './AuthMethodTabs';
import { FieldRenderer } from './FieldRenderer';
import { ConnectorIcon } from './ConnectorIcon';
import {
  AdvancedDisclosure,
  FieldRows,
  InstructionsDisclosure,
  PrerequisiteIpStrip,
  SectionHeader,
} from './ConnectionFormSections';
import {
  advancedSummaryLabels,
  buildConnectionFormLayout,
  buildFieldRows,
  hasAnyValue,
  hasCollectedDescription,
} from './connection-form-layout';
import { useNetworkConfig } from '@/hooks/useFeatures';
import { cn } from '@/lib/utils';

import type { Connector, UIField, ConnectionType, AuthMethod } from '@optidev/shared-types';
import { UI_FIELD_TYPES } from '@optidev/shared-types';

interface FormData {
  name: string;
  authMethod?: string;
  credentials: Record<string, unknown>;
  maskedCredentials?: Record<string, unknown>;
}

interface DynamicConnectionFormProps {
  connector: Connector;
  onSubmit: (data: FormData) => void | Promise<void>;
  onCancel: () => void;
  onOAuthStart?: (authMethod: string, connectionName: string, additionalParams?: Record<string, string>) => void | Promise<void>;
  onReauthorize?: () => void | Promise<void>;
  isSubmitting?: boolean;
  isReauthorizing?: boolean;
  initialData?: Partial<FormData>;
  isEditMode?: boolean;
  authorizedEmail?: string;
  // For resource picker - pass access token after OAuth completes
  accessToken?: string;
  // For Microsoft picker - needed to fetch SharePoint-scoped token
  connectionId?: string;
  // For authenticated API calls (e.g., Microsoft picker-token endpoint)
  getAuthToken?: () => Promise<string | null>;
}

/**
 * DynamicConnectionForm - Schema-driven connection form
 *
 * Renders form based on connector.uiSchema:
 * - Handles multiple auth methods via tabs
 * - Shows OAuth button for oauth2 connection types
 * - Validates required fields before submission
 * - Submits credentials with connection name
 */
export function DynamicConnectionForm({
  connector,
  onSubmit,
  onCancel,
  onOAuthStart,
  onReauthorize,
  isSubmitting = false,
  isReauthorizing = false,
  initialData,
  isEditMode = false,
  authorizedEmail,
  accessToken,
  connectionId,
  getAuthToken,
}: DynamicConnectionFormProps) {
  const { uiSchema, connectionType, name: connectorName } = connector;

  // Get network config for IP whitelist (only shown for database connectors)
  const { networkConfig } = useNetworkConfig();
  const showIpWhitelist = connectionType === 'database' && networkConfig.egressIps.length > 0;

  // Get auth methods from schema (or create default from direct fields)
  // In edit mode, only show the method the connection was created with — the user
  // cannot change auth method on an existing connection.
  const authMethods = useMemo(() => {
    let methods: AuthMethod[] = [];
    if (uiSchema.authMethods && uiSchema.authMethods.length > 0) {
      methods = uiSchema.authMethods;
    } else if (uiSchema.fields && uiSchema.fields.length > 0) {
      methods = [{
        id: 'default',
        label: 'Configure',
        description: '',
        fields: uiSchema.fields,
      }];
    }

    if (isEditMode && initialData?.authMethod) {
      const used = methods.find((m) => m.id === initialData.authMethod);
      if (used) return [used];
    }

    return methods;
  }, [uiSchema, isEditMode, initialData?.authMethod]);

  // Helper to build initial credentials from field defaults
  const buildDefaultCredentials = useCallback((fields: UIField[] | undefined): Record<string, unknown> => {
    if (!fields) return {};
    const defaults: Record<string, unknown> = {};
    fields.forEach((field: UIField) => {
      if (field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });
    return defaults;
  }, []);

  // Get initial auth method's fields for default credentials
  const initialAuthMethodFields = useMemo(() => {
    const initialMethod = authMethods.find((m: AuthMethod) => m.id === (initialData?.authMethod ?? authMethods[0]?.id));
    return initialMethod?.fields;
  }, [authMethods, initialData?.authMethod]);

  // State
  const [connectionName, setConnectionName] = useState(
    initialData?.name ?? `My ${connectorName} Connection`
  );
  const [selectedAuthMethod, setSelectedAuthMethod] = useState(
    initialData?.authMethod ?? authMethods[0]?.id ?? ''
  );
  const [credentials, setCredentials] = useState<Record<string, unknown>>(
    initialData?.credentials ?? buildDefaultCredentials(initialAuthMethodFields)
  );
  const [maskedCredentials, setMaskedCredentials] = useState<Record<string, unknown>>(
    initialData?.maskedCredentials ?? {}
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sync form state when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setConnectionName(initialData.name ?? `My ${connectorName} Connection`);
      setSelectedAuthMethod(initialData.authMethod ?? authMethods[0]?.id ?? '');
      setCredentials(initialData.credentials ?? {});
      setMaskedCredentials(initialData.maskedCredentials ?? {});
      setErrors({});
    }
  }, [initialData, connectorName, authMethods]);

  // Get current auth method fields
  const currentAuthMethod = useMemo(() => {
    return authMethods.find((m: AuthMethod) => m.id === selectedAuthMethod);
  }, [authMethods, selectedAuthMethod]);

  const currentFields = useMemo(() => {
    return currentAuthMethod?.fields ?? [];
  }, [currentAuthMethod]);

  /**
   * Grouping, the instructions disclosure and "Advanced" are all derived from the schema the
   * connector already ships — `field.required` and `field.description`. See
   * `connection-form-layout.ts` for the rules.
   */
  const layout = useMemo(
    () => buildConnectionFormLayout(currentFields, currentAuthMethod, uiSchema.helpText),
    [currentFields, currentAuthMethod, uiSchema.helpText],
  );

  /**
   * Anything already set must never be out of sight, so editing opens "Advanced".
   * Only read when the disclosure mounts — it is keyed on the auth method, so switching tabs
   * re-evaluates it while typing does not fight the user's own open/close.
   */
  const advancedDefaultOpen = useMemo(
    () => isEditMode && hasAnyValue(layout.advancedFields, credentials, maskedCredentials),
    [isEditMode, layout.advancedFields, credentials, maskedCredentials],
  );

  // Check if current auth method has resource picker fields
  const hasResourcePicker = useMemo(() => {
    return currentFields.some((field: UIField) => field.type === UI_FIELD_TYPES.RESOURCE_PICKER);
  }, [currentFields]);

  // Check if this is an OAuth method with resource picker (two-phase flow)
  const isOAuthWithResourcePicker = useMemo(() => {
    return (
      (currentAuthMethod?.resourceScope === 'selected' || hasResourcePicker) &&
      (currentAuthMethod?.id === 'oauth2' ||
        currentAuthMethod?.id === 'oauth' ||
        connectionType === ('oauth' as ConnectionType) ||
        connectionType === ('oauth2' as ConnectionType))
    );
  }, [currentAuthMethod, hasResourcePicker, connectionType]);

  // Check if current auth method is OAuth (matches 'oauth', 'oauth2', 'oauth_readonly', etc.)
  const isOAuthMethod = useMemo(() => {
    return (
      selectedAuthMethod.startsWith('oauth') ||
      (connectionType === 'oauth2' as ConnectionType && authMethods.length <= 1)
    );
  }, [selectedAuthMethod, connectionType, authMethods]);

  /**
   * Update a credential field.
   *
   * Deliberately silent: every value passing through here is a customer credential, and two
   * console.log calls used to print them — the field on each change, and the whole
   * accumulated object after. A real Databricks OAuth secret was recovered from a bug-report
   * screenshot that way. Console output reaches further than it looks: DevTools during a
   * screen-share, browser extensions with tab access, and any session-replay or error
   * reporter that captures console. Do not add logging here; if you need to debug the form,
   * log field NAMES only, never values.
   */
  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    setCredentials((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    // Clear error when user types
    if (errors[fieldName]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
  }, [errors]);

  // Handle auth method change
  const handleAuthMethodChange = useCallback((methodId: string) => {
    setSelectedAuthMethod(methodId);
    // Apply default values for the new auth method's fields
    const newMethod = authMethods.find((m: AuthMethod) => m.id === methodId);
    setCredentials(buildDefaultCredentials(newMethod?.fields));
    setErrors({});
  }, [authMethods, buildDefaultCredentials]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Skip name validation when in resource picker phase (name is auto-generated by backend)
    const isResourcePickerPhase = isOAuthWithResourcePicker && accessToken && !isEditMode;

    // Validate connection name (unless in resource picker phase)
    if (!isResourcePickerPhase && !connectionName.trim()) {
      newErrors['_name'] = 'Connection name is required';
    }

    // Validate required fields (skip for pure OAuth methods without pre-OAuth fields)
    // Before OAuth: skip resource_picker fields (they require OAuth token to show)
    if (!isOAuthMethod || (isOAuthMethod && currentFields.length > 0)) {
      const fieldsToValidate = isOAuthMethod && !accessToken
        ? currentFields.filter((f: UIField) => f.type !== UI_FIELD_TYPES.RESOURCE_PICKER)
        : currentFields;
      fieldsToValidate.forEach((field: UIField) => {
        if (field.required) {
          const value = credentials[field.name];
          if (value === undefined || value === null || value === '') {
            newErrors[field.name] = `${field.label} is required`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [connectionName, currentFields, credentials, isOAuthMethod, isOAuthWithResourcePicker, accessToken, isEditMode]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      await onSubmit({
        name: connectionName.trim(),
        authMethod: selectedAuthMethod,
        credentials,
      });
    },
    [validateForm, onSubmit, connectionName, selectedAuthMethod, credentials]
  );

  /**
   * One renderer for every field on the form. `collectDescription` suppresses the inline helper
   * line for the fields whose wording now lives in the "Where do I find these?" disclosure.
   */
  const renderField = useCallback(
    (field: UIField, collectDescription = false) => (
      <FieldRenderer
        key={field.name}
        field={field}
        value={credentials[field.name]}
        maskedValue={maskedCredentials[field.name]}
        onChange={(value) => handleFieldChange(field.name, value)}
        error={errors[field.name]}
        accessToken={accessToken}
        connectionId={connectionId}
        getAuthToken={getAuthToken}
        hideDescription={collectDescription && hasCollectedDescription(field)}
      />
    ),
    [credentials, maskedCredentials, errors, accessToken, connectionId, getAuthToken, handleFieldChange],
  );

  // Handle OAuth button click
  const handleOAuthClick = useCallback(async () => {
    if (!validateForm()) {
      return;
    }
    if (onOAuthStart) {
      // Pass pre-OAuth field values (e.g., shopDomain) as additionalParams
      const additionalParams = currentFields.length > 0
        ? (credentials as Record<string, string>)
        : undefined;
      await onOAuthStart(selectedAuthMethod, connectionName.trim(), additionalParams);
    }
  }, [validateForm, connectionName, onOAuthStart, selectedAuthMethod, credentials, currentFields]);

  // OAuth finished and the user is now picking files — the name is generated by the backend.
  const isResourcePickerPhase = isOAuthWithResourcePicker && !!accessToken && !isEditMode;

  const connectionNameField = (
    <div className="space-y-2">
      <Label htmlFor="connection-name">
        Connection name <span className="text-destructive">*</span>
      </Label>
      <Input
        id="connection-name"
        value={connectionName}
        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
        onChange={(e) => {
          setConnectionName(e.target.value);
          if (errors['_name']) {
            setErrors((prev) => {
              const next = { ...prev };
              delete next['_name'];
              return next;
            });
          }
        }}
        placeholder="Enter a name for this connection"
        className={errors['_name'] ? 'border-destructive' : ''}
      />
      {errors['_name'] && (
        <p className="text-xs text-destructive">{errors['_name']}</p>
      )}
    </div>
  );

  const authMethodTabs = authMethods.length > 1 ? (
    <AuthMethodTabs
      authMethods={authMethods}
      selected={selectedAuthMethod}
      onSelect={handleAuthMethodChange}
      brandTheme={connector.brandTheme}
      // On the OAuth path the authorization card repeats this sentence verbatim.
      showDescription={!isOAuthMethod}
    />
  ) : null;

  /**
   * Every path gets exactly one primary action in the footer, beside Cancel — including OAuth
   * sign-in, which used to be a full-width button in the body with a Cancel-only row beneath it.
   */
  const isOAuthSignInPhase = isOAuthMethod && !isEditMode && !isResourcePickerPhase;
  const brandButtonStyle = connector.brandTheme
    ? {
        backgroundColor: connector.brandTheme.primary,
        color: connector.brandTheme.primaryForeground || '#ffffff',
      }
    : undefined;

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      {/* Header with connector info - stays put while the fields scroll */}
      <div className="flex flex-none items-center gap-3 border-b border-border px-6 py-4 pr-12">
        <ConnectorIcon
          connector={connector}
          className="h-7 w-7 flex-shrink-0"
        />
        <div className="min-w-0">
          <h3 className="font-semibold text-sm">
            {isEditMode ? `Edit ${connectorName} Connection` : connectorName}
          </h3>
          {!isEditMode && connector.description && (
            <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{connector.description}</p>
          )}
          {isEditMode && (
            <p className="text-xs text-muted-foreground mt-0.5">Update connection credentials</p>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
      {/*
        The firewall IP is reference information, not an input, and it has to be done before any
        of these credentials will work — so it sits above the form rather than between fields.
      */}
      {showIpWhitelist && <PrerequisiteIpStrip egressIps={networkConfig.egressIps} />}

      {/* Structured, schema-driven layout for credential forms (databases, API keys) */}
      {!isOAuthMethod && (
        <div className="space-y-5">
          {layout.sections.map((section, index) => (
            <div
              key={section.id}
              className={cn('space-y-4', index > 0 && 'border-t border-border pt-4')}
            >
              {layout.useSections && <SectionHeader title={section.title} />}
              {index === 0 && connectionNameField}
              {index === 0 && authMethodTabs}
              {section.fields.length > 0 && (
                <FieldRows
                  rows={buildFieldRows(section.fields)}
                  renderField={(field) => renderField(field, true)}
                />
              )}
            </div>
          ))}

          <InstructionsDisclosure
            instructions={layout.instructions}
            title={layout.instructionsTitle}
          />

          {layout.advancedFields.length > 0 && (
            <AdvancedDisclosure
              // Remount per auth method so `defaultOpen` is re-evaluated when tabs switch.
              key={selectedAuthMethod}
              labels={advancedSummaryLabels(layout.advancedFields)}
              defaultOpen={advancedDefaultOpen}
            >
              <FieldRows
                rows={buildFieldRows(layout.advancedFields)}
                renderField={(field) => renderField(field, true)}
              />
            </AdvancedDisclosure>
          )}
        </div>
      )}

      {/* OAuth forms keep the name and method tabs up front, above the authorization card */}
      {isOAuthMethod && !isResourcePickerPhase && connectionNameField}
      {isOAuthMethod && authMethodTabs}

      {/* OAuth section - Edit mode: show authorized status + reauthorize button */}
      {isOAuthMethod && isEditMode && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
            <h4 className="font-medium text-sm">Authorization</h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm">
                  Authorized as <strong>{authorizedEmail || 'Unknown'}</strong>
                </span>
              </div>
              {onReauthorize && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onReauthorize}
                  disabled={isReauthorizing}
                >
                  {isReauthorizing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="mr-2 h-4 w-4" />
                  )}
                  {isReauthorizing ? 'Reauthorizing...' : 'Reauthorize'}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Reauthorize to switch accounts or update permissions.
            </p>
          </div>

          {/* Show resource picker fields in edit mode - token can be fetched lazily via connectionId */}
          {isOAuthWithResourcePicker && (accessToken || connectionId) && currentFields.length > 0 && (
            <div className="space-y-4">
              {currentFields.map((field: UIField) => renderField(field))}
            </div>
          )}
        </div>
      )}

      {/* OAuth section - Create mode: show sign-in button (hide after OAuth completes for resource picker flow) */}
      {isOAuthMethod && !isEditMode && !(isOAuthWithResourcePicker && accessToken) && (
        <div className="space-y-4">
          {/* Pre-OAuth fields (e.g., shopDomain for Shopify) - exclude resource_picker which needs OAuth token */}
          {currentFields.filter((f) => f.type !== UI_FIELD_TYPES.RESOURCE_PICKER).length > 0 && (
            <div className="space-y-4">
              {currentFields
                .filter((field: UIField) => field.type !== UI_FIELD_TYPES.RESOURCE_PICKER)
                .map((field: UIField) => renderField(field))}
            </div>
          )}

          {/* OAuth info card */}
          <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-3">
            <h4 className="font-medium text-sm">
              {(currentAuthMethod as { cardTitle?: string })?.cardTitle || currentAuthMethod?.label || 'OAuth 2.0 Authentication'}
            </h4>
            {currentAuthMethod?.description && (
              <p className="text-sm text-muted-foreground">
                {currentAuthMethod.description}
              </p>
            )}
            <ul className="space-y-1.5">
              {(
                (currentAuthMethod as { features?: string[] })?.features ?? [
                  'Secure OAuth 2.0 authentication',
                  'Automatic token refresh',
                  'Revoke access anytime',
                ]
              ).map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* The sign-in button itself lives in the footer, beside Cancel */}

          {currentAuthMethod?.helpText && (
            <p className="text-xs text-muted-foreground">
              {currentAuthMethod.helpText}
            </p>
          )}
        </div>
      )}

      {/* Resource picker fields - for OAuth methods with resource picker (shown after OAuth) */}
      {isResourcePickerPhase && currentFields.length > 0 && (
        <div className="space-y-4">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950 p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-800 dark:text-green-200">
                Connected to {connectorName}
              </span>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300">
              Now select which files you want to access.
            </p>
          </div>

          {currentFields.map((field: UIField) => renderField(field))}

          {/* Help text */}
          {currentAuthMethod?.helpText && (
            <p className="text-sm text-muted-foreground">
              {currentAuthMethod.helpText}
            </p>
          )}
        </div>
      )}

      {/*
        Schema-level help. The structured path folds it into the instructions disclosure as
        "Setup notes"; the OAuth path has no disclosure, so it still renders inline there.
      */}
      {uiSchema.helpText && isOAuthMethod && (
        <p className="text-sm text-muted-foreground">{uiSchema.helpText}</p>
      )}
      {uiSchema.helpUrl && (
        <a
          href={uiSchema.helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          View documentation
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
      </div>

      {/*
        Sticky footer. On the long database forms the Connect button used to scroll below the
        fold, so the action is pinned and Cancel gets an explicit control.
      */}
      <div className="flex flex-none items-center gap-3 border-t border-border bg-card px-6 py-3.5">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isSubmitting || isReauthorizing}
        >
          Cancel
        </Button>

        {isOAuthSignInPhase ? (
          <Button
            type="button"
            className="ml-auto"
            onClick={handleOAuthClick}
            disabled={isSubmitting}
            style={brandButtonStyle}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {/* The header already names the connector, so narrow dialogs drop it rather than wrap */}
            Sign in<span className="hidden sm:inline">&nbsp;with {connectorName}</span>
          </Button>
        ) : (
          <Button
            type="submit"
            className="ml-auto"
            disabled={isSubmitting || isReauthorizing}
            style={!isEditMode ? brandButtonStyle : undefined}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditMode || isResourcePickerPhase ? 'Saving...' : 'Connecting...'}
              </>
            ) : isResourcePickerPhase ? (
              'Save Connection'
            ) : isEditMode ? (
              'Save Changes'
            ) : (
              'Connect'
            )}
          </Button>
        )}
      </div>
    </form>
  );
}

export default DynamicConnectionForm;
